import { faBan, faEye } from "@fortawesome/pro-regular-svg-icons";
import { Cell, ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { groupBy, isNil, isObject, isString } from "lodash";
import { twMerge } from "tailwind-merge";

import {
  DecisionsOutcomeFilter,
  DevisionHistoryV3GetRequest,
  EqualityOperator,
  MultipleOutcomeFilter,
  OutcomePresenceFilter,
  SingleOutcomeFilter,
} from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { Flow, FlowVersion, SchemaTypes } from "src/api/flowTypes";
import { DecisionEnvironment } from "src/api/types";
import { ColumnFilter } from "src/base-components/ColumnFilter";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import { useChangeHistories } from "src/changeHistory/queries";
import {
  GetDecisionsParamsBaseOutputCaseStatusEnum,
  HistoricalDecisionApiResponse,
} from "src/clients/history_v3";
import { RawCell } from "src/dataTable/RawCell";
import { ResultCell } from "src/dataTable/ResultCell";
import { useCancelDecision } from "src/decisionsOverview/queries";
import { outcomeFieldsPrefix } from "src/decisionsOverview/useViewOptions";
import {
  isValidEntityId,
  OutcomeItem,
  transformSchemaToOptions,
} from "src/decisionsOverview/utils";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { getSourceDetails } from "src/flow/decisionHistory/DecisionHistoryUtils";
import { DateRangeFilter } from "src/flow/decisionHistory/filters/DateRangeFilter";
import { EnumFilter } from "src/flow/decisionHistory/filters/EnumFilter";
import { OutcomeFieldFilter } from "src/flow/decisionHistory/filters/OutcomeFieldFilter";
import { OriginColumnFilter } from "src/flow/decisionHistory/OriginColumnFilter";
import {
  DecisionStatusCode,
  POSSIBLE_DECISION_STATUS_CODES,
  SharedDecisionHistoryStatus,
} from "src/flow/decisionHistory/SharedStatusColumn";
import { SourcePill } from "src/flow/decisionHistory/SourcePill";
import {
  getParentFlowsWithVersions,
  singleFlowGetParentFlows,
  singleFlowGetPotentialParentVersions,
} from "src/flow/Model";
import { Job } from "src/jobs/types";
import { ReviewCaseStatus } from "src/manualReview/ReviewCaseStatus";
import { BOOLEAN_FILTERS, OUTCOME_KEY_FILTERS } from "src/outcomes/constants";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToDecisionsOverview } from "src/router/urls";
import { isJsonRefPath } from "src/schema/jsonRefUtils";
import { formatDate } from "src/utils/datetime";
import { formatDuration } from "src/utils/numbers";
import { errorMessage } from "src/utils/stringUtils";
import {
  getDecisionDisabledReason,
  getDisableReasonMessage,
} from "src/versionDecisionHistory/Table";

export type TracingHoverBehavior = "trace" | "expand";

type GetDecisionHistoryColumnsArgs = {
  orgId: string;
  wsId: string;
  flow: Flow;
  jobs: Job[];
  filterFields: DevisionHistoryV3GetRequest;
  changeFilterFields: (fields: DevisionHistoryV3GetRequest) => void;
  availableFlowVersions: FlowVersion[];
  env: DecisionEnvironment;
  outcomeOptions: OutcomeItem[];
  includeEntityLinks: boolean;
  includeInputOutput: boolean;
  includeEntityId: boolean;
  tracingHoverBehavior?: TracingHoverBehavior;
  cancellingDecisionIds?: string[];
  setCancellingDecisionIds: (updater: (prev: string[]) => string[]) => void;
};

export enum DecisionHistoryColumns {
  DecisionId = "decision.decision_id",
  EntityId = "decision.entity_id",
  Datetime = "decision.datetime",
  Status = "decision.status",
  Origin = "decision.origin",
  Duration = "decision.duration",
  Version = "decision.version",
}

export const OPTIONS_COLUMN_ID = "options-group";

export type ExtendedHistoricalDecisionApiResponse =
  HistoricalDecisionApiResponse & {
    outcome?: Record<string, unknown>;
  };

const columnHelper =
  createColumnHelper<ExtendedHistoricalDecisionApiResponse>();

const availableStatusCodes = POSSIBLE_DECISION_STATUS_CODES.map(
  (option) => option.key,
);

const HeaderCell: React.FC<{
  children?: React.ReactNode;
  className: string;
}> = ({ children, className }) => (
  <div
    className={twMerge(
      "whitespace-nowrap p-1.5 text-left font-inter-medium-12px text-gray-800",
      className,
    )}
  >
    {children}
  </div>
);

const TypedFieldHeader: React.FC<{
  label: string;
  type: SchemaTypes;
  children?: React.ReactNode;
}> = ({ label, type, children }) => (
  <HeaderCell className="flex flex-shrink-0 items-center gap-x-1">
    <Tooltip placement="bottom" title={type} asChild>
      <Icon
        icon={
          isJsonRefPath(type)
            ? SCHEMA_TYPE_ICONS["entity"]
            : SCHEMA_TYPE_ICONS[type]
        }
        size="xs"
      />
    </Tooltip>
    <span className="inline-block">{label}</span>
    {children}
  </HeaderCell>
);

const GroupHeader: React.FC<{ label: string; children?: React.ReactNode }> = ({
  label,
  children,
}) => (
  <HeaderCell className="flex h-9 flex-shrink-0 items-center gap-x-1 bg-gray-50 py-2">
    <span className="inline-block">{label}</span>
    {children}
  </HeaderCell>
);

export const getDecisionHistoryColumns = ({
  orgId,
  wsId,
  flow,
  jobs,
  changeFilterFields,
  filterFields,
  availableFlowVersions,
  env,
  includeEntityLinks,
  includeInputOutput,
  includeEntityId,
  outcomeOptions,
  tracingHoverBehavior,
  cancellingDecisionIds,
  setCancellingDecisionIds,
}: GetDecisionHistoryColumnsArgs): ColumnDef<
  ExtendedHistoricalDecisionApiResponse,
  unknown
>[] => {
  const { input: inputOptions, output: outputOptions } = includeInputOutput
    ? transformSchemaToOptions(flow.versions)
    : { input: [], output: [] };

  const resetFieldFilter = (key: keyof DevisionHistoryV3GetRequest) => {
    const { [key]: _, ...fieldsWithoutStatusCode } = filterFields;
    return changeFilterFields(fieldsWithoutStatusCode);
  };

  const setStatusCodeFilter = (
    newStatusCode: (typeof POSSIBLE_DECISION_STATUS_CODES)[number]["key"],
  ) => {
    // if selected field is the same than the previously selected
    // then we remove the filter.
    if (newStatusCode === filterFields.statusCodes) {
      resetFieldFilter("statusCodes");
    } else {
      const currentFields = filterFields;
      const newFields = { ...currentFields, statusCodes: newStatusCode };
      changeFilterFields(newFields);
    }
  };

  const setVersionIdFilter = (newVersionId: string) => {
    // if selected field is the same than the previously selected
    // then we remove the filter.
    if (newVersionId === filterFields.flowVersionIds) {
      resetFieldFilter("flowVersionIds");
    } else {
      const currentFields = filterFields;
      const newFields = { ...currentFields, flowVersionIds: newVersionId };
      changeFilterFields(newFields);
    }
  };

  const setOutcomeFilter = (
    outcomeTypeKey: string,
    value: DecisionsOutcomeFilter["filter"],
  ) => {
    const propertyKey = isObject(value) ? value.property : null;
    const sameKeyFilter = filterFields.outcomeFilters?.find(
      (f) =>
        f.outcome_type_key === outcomeTypeKey &&
        (propertyKey
          ? isObject(f.filter) && f.filter.property === propertyKey
          : isString(f.filter)),
    );

    // If no such filter or the value is different, we set the filter
    if (!sameKeyFilter || sameKeyFilter.filter !== value) {
      const newFilter: DecisionsOutcomeFilter = {
        outcome_type_key: outcomeTypeKey,
        filter: value,
      };

      changeFilterFields({
        ...filterFields,
        outcomeFilters: [
          ...(filterFields.outcomeFilters ?? []).filter(
            (fltr) => fltr !== sameKeyFilter,
          ),
          newFilter,
        ],
      });
    } else {
      // If the value is the same, we remove the filter
      changeFilterFields({
        ...filterFields,
        outcomeFilters: (filterFields.outcomeFilters ?? []).filter(
          (fltr) => fltr !== sameKeyFilter,
        ),
      });
    }
  };

  const setOutcomeDateFilter = (
    outcomeTypeKey: string,
    values: SingleOutcomeFilter["filter"][],
  ) => {
    const firstFilter = values[0];
    const propertyKey = firstFilter.property;
    const sameKeyFilters = filterFields.outcomeFilters?.filter(
      (f) =>
        f.outcome_type_key === outcomeTypeKey &&
        propertyKey &&
        isObject(f.filter) &&
        f.filter.property === propertyKey,
    );

    const newFilters = values.map((filter) => ({
      outcome_type_key: outcomeTypeKey,
      filter,
    }));

    changeFilterFields({
      ...filterFields,
      outcomeFilters: [
        ...(filterFields.outcomeFilters ?? []).filter(
          (fltr) =>
            !sameKeyFilters?.some((sameKeyFilter) => sameKeyFilter === fltr),
        ),
        ...newFilters,
      ],
    });
  };

  const resetOutcomeFilter = (
    outcomeTypeKey: string,
    fieldKey: string | null,
  ) => {
    changeFilterFields({
      ...filterFields,
      outcomeFilters: (filterFields.outcomeFilters ?? []).filter((fltr) => {
        if (fieldKey === null) {
          return !(
            fltr.outcome_type_key === outcomeTypeKey &&
            typeof fltr.filter === "string"
          );
        }

        return !(
          fltr.outcome_type_key === outcomeTypeKey &&
          isObject(fltr.filter) &&
          fltr.filter.property === fieldKey
        );
      }),
    });
  };

  const statusFilterOptions = POSSIBLE_DECISION_STATUS_CODES.map((option) => ({
    ...option,
    disabled: !availableStatusCodes.includes(option.key),
  }));

  const parentFlows = singleFlowGetParentFlows(flow);
  const potentialParentVersions = singleFlowGetPotentialParentVersions(flow);
  const parentFlowsWithVersions = getParentFlowsWithVersions(flow);

  const columns: ColumnDef<HistoricalDecisionApiResponse, unknown>[] = [
    columnHelper.group({
      id: "metadata",
      header: () => <GroupHeader label="Metadata" />,
      columns: [
        columnHelper.accessor("id", {
          id: DecisionHistoryColumns.DecisionId,
          size: 50,
          header: () => (
            <HeaderCell className="flex items-center">
              <span className="inline-block">Decision ID</span>
            </HeaderCell>
          ),
          cell: ({ cell }) => {
            const value = cell.getValue();
            return <RawCell cell={<TruncatedIdPill id={value ?? ""} />} />;
          },
        }),
        includeEntityId
          ? columnHelper.accessor("entity_id", {
              id: DecisionHistoryColumns.EntityId,
              size: 50,
              header: () => (
                <HeaderCell className="flex items-center">
                  <span className="inline-block">Entity ID</span>
                </HeaderCell>
              ),
              cell: ({ cell }) => {
                const value = cell.getValue();
                return (
                  <RawCell<HistoricalDecisionApiResponse>
                    cell={
                      value ? (
                        <TruncatedIdPill id={value} />
                      ) : (
                        <div className="ml-1">—</div>
                      )
                    }
                  />
                );
              },
            })
          : false,
        columnHelper.accessor("start_time", {
          id: DecisionHistoryColumns.Datetime,
          size: 70,
          header: () => <HeaderCell className="w-24">Time</HeaderCell>,
          cell: ({ cell }) => {
            const value = cell.getValue();
            const renderValue = value
              ? formatDate(value, "dd MMM yy HH:mm:ss")
              : "N/A";
            return (
              <RawCell<HistoricalDecisionApiResponse>
                cell={<>{renderValue}</>}
                classNameOverrides="max-w-none w-36 p-1.5"
              />
            );
          },
        }),
        columnHelper.accessor(
          (row) => {
            if (
              row.cases &&
              row.cases.length > 0 &&
              row.cases[0]?.status !== "completed"
            ) {
              return {
                isCase: true,
                status: row.cases[0]?.status,
              };
            }

            return {
              isCase: false,
              status: row["status_code"],
            };
          },
          {
            id: DecisionHistoryColumns.Status,
            size: 200,
            header: () => (
              <HeaderCell className="flex w-auto items-center">
                <span>Status</span>
                <span className="ml-1">
                  <ColumnFilter
                    description="Filter by status"
                    elements={statusFilterOptions}
                    isFiltering={Boolean(filterFields.statusCodes)}
                    placement="bottom start"
                    selected={filterFields.statusCodes ?? undefined}
                    onResetRequest={() => resetFieldFilter("statusCodes")}
                    onSelect={(value) =>
                      setStatusCodeFilter(
                        value as (typeof availableStatusCodes)[number],
                      )
                    }
                  />
                </span>
              </HeaderCell>
            ),
            cell: ({ cell }) => {
              const { isCase, status } = cell.getValue();
              const isCancelling =
                !!cell.row.original.id &&
                cancellingDecisionIds?.includes(cell.row.original.id);
              const message = (
                <span className="justify-start">
                  {isCase && status ? (
                    <ReviewCaseStatus
                      status={
                        status as GetDecisionsParamsBaseOutputCaseStatusEnum
                      }
                    />
                  ) : (
                    <SharedDecisionHistoryStatus
                      isCancelling={isCancelling}
                      statusCode={status ?? ""}
                    />
                  )}
                </span>
              );

              return (
                <ResultCell<HistoricalDecisionApiResponse>
                  cell={message}
                  className="max-w-none overflow-auto p-1.5"
                  objectDetailPosition="topRight"
                />
              );
            },
          },
        ),
        columnHelper.accessor(
          (row) =>
            getSourceDetails(row, parentFlows, potentialParentVersions, jobs),
          {
            id: DecisionHistoryColumns.Origin,
            size: 100,
            header: () => {
              return (
                <HeaderCell className="flex w-24 items-center">
                  Origin{" "}
                  <div>
                    <OriginColumnFilter
                      env={env}
                      flows={parentFlowsWithVersions}
                      jobs={jobs}
                      placement="bottom start"
                      selected={filterFields}
                      onSelect={changeFilterFields}
                    />
                  </div>
                </HeaderCell>
              );
            },
            cell: ({ cell }) => {
              const sourceDetails = cell.getValue();
              const content = (
                <SourcePill
                  decisionEnv={env}
                  orgId={orgId}
                  sourceDetails={sourceDetails}
                  wsId={wsId}
                />
              );

              return (
                <RawCell<HistoricalDecisionApiResponse>
                  cell={content}
                  classNameOverrides="max-w-[300px] w-auto p-1.5"
                />
              );
            },
          },
        ),
        columnHelper.accessor(
          (row) =>
            new Date(row.end_time ?? new Date()).getTime() -
            new Date(row.start_time ?? new Date()).getTime(),
          {
            id: DecisionHistoryColumns.Duration,
            size: 100,
            header: () => (
              <HeaderCell className="flex items-center">
                <span className="inline-block">Duration</span>
              </HeaderCell>
            ),
            cell: ({ cell }) => {
              const durationMs = cell.getValue();
              const displayDuration = (
                <span className="w-full">
                  <Tooltip
                    disabled={durationMs <= 1000}
                    placement="top"
                    title={`${durationMs}ms`}
                  >
                    {formatDuration(durationMs, { onlyFirstUnit: true })}
                  </Tooltip>
                </span>
              );
              return (
                <RawCell<HistoricalDecisionApiResponse>
                  cell={displayDuration}
                  classNameOverrides="max-w-0 p-1.5"
                />
              );
            },
          },
        ),
        columnHelper.accessor("flow.version.name", {
          id: DecisionHistoryColumns.Version,
          size: 100,
          header: () => (
            <HeaderCell className="flex items-center">
              <span className="inline-block">Version</span>
              <span className="ml-1">
                <ColumnFilter
                  description="Filter by version"
                  elements={availableFlowVersions.map((version) => ({
                    key: version.id,
                    value: version.name,
                  }))}
                  isFiltering={Boolean(filterFields.flowVersionIds)}
                  placement="bottom start"
                  selected={filterFields.flowVersionIds ?? undefined}
                  onResetRequest={() => resetFieldFilter("flowVersionIds")}
                  onSelect={(key) => setVersionIdFilter(key)}
                />
              </span>
            </HeaderCell>
          ),
          cell: ({ cell }) => (
            <VersionCell
              cell={<span className="w-full">{cell.getValue()}</span>}
            />
          ),
        }),
      ].filter(Boolean) as ColumnDef<HistoricalDecisionApiResponse, unknown>[],
    }),
  ];

  if (inputOptions.length > 0) {
    columns.push(
      columnHelper.group({
        id: "input",
        header: () => <GroupHeader label="Inputs" />,
        columns: inputOptions.map(({ label, value, type }) =>
          columnHelper.accessor(
            (row) => (row.input as Record<string, unknown>)[label],
            {
              id: value,
              size: 100,
              header: () => <TypedFieldHeader label={label} type={type} />,
              cell: ({ cell }) => (
                <ResultCell<HistoricalDecisionApiResponse>
                  cell={cell}
                  className="max-w-0 overflow-auto p-1.5"
                  objectDetailPosition="auto"
                />
              ),
            },
          ),
        ),
      }),
    );
  }

  if (outputOptions.length > 0) {
    columns.push(
      columnHelper.group({
        id: "output",
        header: () => <GroupHeader label="Outputs" />,
        columns: outputOptions.map(({ label, value, type }) =>
          columnHelper.accessor(
            (row) => (row.output as Record<string, unknown> | null)?.[label],
            {
              id: value,
              size: 100,
              header: () => <TypedFieldHeader label={label} type={type} />,
              cell: ({ cell }) => (
                <ResultCell<HistoricalDecisionApiResponse>
                  cell={cell}
                  className="max-w-0 overflow-auto p-1.5"
                  objectDetailPosition="auto"
                />
              ),
            },
          ),
        ),
      }),
    );
  }

  if (outcomeOptions.length > 0) {
    columns.push(
      ...Object.entries(groupBy(outcomeOptions, "outcomeName")).map(
        ([outcomeName, outcomeItems]) => {
          const groupKey = outcomeItems[0].outcomeKey;
          const groupKeyWithoutPrefix = groupKey.replace(
            new RegExp(`^${outcomeFieldsPrefix}`),
            "",
          );

          return columnHelper.group({
            id: groupKey,
            header: () => {
              const outcomeFilter = filterFields.outcomeFilters?.find(
                (filter) =>
                  filter.outcome_type_key === groupKeyWithoutPrefix &&
                  isString(filter.filter),
              ) as OutcomePresenceFilter | null;

              return (
                <GroupHeader label={outcomeName}>
                  <span className="ml-1">
                    <ColumnFilter
                      dataLoc={`decision-history-outcome-filter-${groupKeyWithoutPrefix}`}
                      description="Filter by outcome"
                      elements={OUTCOME_KEY_FILTERS}
                      isFiltering={Boolean(outcomeFilter)}
                      placement="bottom start"
                      selected={outcomeFilter?.filter ?? undefined}
                      onResetRequest={() =>
                        resetOutcomeFilter(groupKeyWithoutPrefix, null)
                      }
                      onSelect={(value) =>
                        setOutcomeFilter(
                          groupKeyWithoutPrefix,
                          value as DecisionsOutcomeFilter["filter"],
                        )
                      }
                    />
                  </span>
                </GroupHeader>
              );
            },
            columns: outcomeItems.map(
              ({ label, value, type, filterable, enumValues }) =>
                columnHelper.accessor(value as `outcome.${string}`, {
                  id: value,
                  header: () => (
                    <TypedFieldHeader label={label} type={type}>
                      {filterable && (
                        <OutcomeColumnFilter
                          enumValues={enumValues}
                          filters={filterFields.outcomeFilters}
                          propertyPath={value.replace(
                            new RegExp(`^${outcomeFieldsPrefix}`),
                            "",
                          )}
                          type={type}
                          onReset={resetOutcomeFilter}
                          onSelect={setOutcomeFilter}
                          onSelectDate={setOutcomeDateFilter}
                        />
                      )}
                    </TypedFieldHeader>
                  ),
                  size: 100,
                  cell: ({ cell, row }) => {
                    if (row.original.outcome === undefined) {
                      return <SkeletonPlaceholder height="h-4" width="w-4/5" />;
                    }
                    return (
                      <ResultCell<ExtendedHistoricalDecisionApiResponse>
                        cell={cell}
                        className="max-w-0 overflow-auto p-1.5"
                        objectDetailPosition="auto"
                      />
                    );
                  },
                }),
            ),
          });
        },
      ),
    );
  }

  if (tracingHoverBehavior || includeEntityLinks) {
    columns.push(
      columnHelper.group({
        id: OPTIONS_COLUMN_ID,
        header: () => <GroupHeader label="" />,
        columns:
          tracingHoverBehavior || includeEntityLinks
            ? [
                columnHelper.accessor(() => "", {
                  id: "options",
                  size: 50,
                  maxSize: 50,
                  header: () => " ",
                  cell: ({ cell }) => (
                    <OptionsCell
                      availableFlowVersions={availableFlowVersions}
                      cancellingDecisionIds={cancellingDecisionIds}
                      cell={cell}
                      decisionEnv={env}
                      includeEntityLink={includeEntityLinks}
                      setCancellingDecisionIds={setCancellingDecisionIds}
                      tracingHoverBehavior={tracingHoverBehavior}
                    />
                  ),
                }),
              ]
            : [],
      }),
    );
  }

  return columns;
};

const OptionsCell: React.FC<{
  availableFlowVersions: FlowVersion[];
  cell: Cell<HistoricalDecisionApiResponse, unknown>;
  includeEntityLink: boolean;
  tracingHoverBehavior?: TracingHoverBehavior;
  decisionEnv: DecisionEnvironment;
  cancellingDecisionIds?: string[];
  setCancellingDecisionIds: (updater: (prev: string[]) => string[]) => void;
}> = ({
  cell,
  availableFlowVersions,
  includeEntityLink,
  tracingHoverBehavior,
  decisionEnv,
  cancellingDecisionIds,
  setCancellingDecisionIds,
}) => {
  const { orgId, workspace } = useWorkspaceContext();
  const decisionVersion = availableFlowVersions.find(
    (version) => version.id === cell.row.original?.flow?.version?.id,
  );
  const historyChanges = useChangeHistories(
    decisionVersion
      ? [
          {
            versionId: decisionVersion?.id,
            etag: decisionVersion?.etag ?? "00000000",
          },
        ]
      : [],
  );

  const { mutate: cancelDecision } = useCancelDecision(
    workspace.base_url!,
    decisionEnv,
  );

  const handleCancelDecision = () => {
    const decisionId = cell?.row?.original?.id ?? "";
    cancelDecision(
      {
        flowSlug: cell?.row?.original?.flow?.slug ?? "",
        decisionId,
      },
      {
        onSuccess: () => {
          setCancellingDecisionIds((prev) => [...prev, decisionId]);
          toastActions.success({
            title: "Successfully added to cancelling list",
            description:
              "It may take a few minutes for the decision to be cancelled.",
          });
        },
        onError: (error) => {
          toastActions.failure({
            title: "Failed to cancel the decision",
            description: errorMessage(error),
          });
        },
      },
    );
  };

  const disableReason =
    tracingHoverBehavior === "trace"
      ? getDecisionDisabledReason(
          cell.row.original,
          historyChanges?.[0]?.data?.etag,
          historyChanges?.[0]?.data?.created_at,
        )
      : null;

  const canDisplayEntityLink =
    includeEntityLink &&
    isValidEntityId(cell.row.original.entity_id ?? undefined);

  const handleAction = () => {
    window.open(
      getBaseUrl() +
        getUrlToDecisionsOverview(orgId, workspace.id, {
          entityId: cell.row.original.entity_id ?? undefined,
        }),
      "_blank",
    );
  };

  const isPending =
    cell.row.original.status_code === ("202" as DecisionStatusCode);
  const isChildDecision = !!cell?.row?.original?.origin?.parent_decision_id;
  const getEntityLinkElements = () => {
    const entityLinkElements: OptionsDropdownElement[] = [];
    const decisionId = cell?.row?.original?.id ?? "";
    const isCancelling = cancellingDecisionIds?.includes(decisionId) ?? false;

    if (isPending && !isChildDecision && !isCancelling) {
      entityLinkElements.push({
        key: "Cancel decision",
        action: () => handleCancelDecision(),
      });
    } else if (canDisplayEntityLink) {
      entityLinkElements.push({
        key: "nav",
        nav: "View related decisions",
        subtitle: (
          <>
            Show all decisions with the same Entity ID{" "}
            <Pill size="sm" variant="gray" maxWidth>
              <Pill.Text fontType="code">
                {cell.row.original.entity_id}
              </Pill.Text>
            </Pill>
          </>
        ),
        action: handleAction,
      });
    }
    return entityLinkElements;
  };

  const entityLinkElements = getEntityLinkElements();

  if (!tracingHoverBehavior && entityLinkElements.length === 0) {
    return null;
  }

  const message = (
    <div className="invisible flex w-full items-center justify-end gap-x-2 group-hover/row:visible">
      {tracingHoverBehavior &&
        (disableReason ? (
          <Tooltip
            placement="top"
            title={getDisableReasonMessage(disableReason)}
          >
            <Icon icon={faBan} size="sm" />
          </Tooltip>
        ) : (
          <Icon icon={faEye} size="sm" />
        ))}

      {entityLinkElements?.length > 0 ? (
        <EllipsisOptionsDropdown elements={entityLinkElements} />
      ) : null}
    </div>
  );

  return (
    <RawCell<HistoricalDecisionApiResponse>
      cell={message}
      classNameOverrides="max-w-full w-full p-1.5"
    />
  );
};

const VersionCell: React.FC<{
  cell: React.ReactNode;
}> = ({ cell }) => {
  const message = (
    <Pill size="sm" variant="gray">
      <Pill.Text>{cell}</Pill.Text>
    </Pill>
  );
  return (
    <RawCell<HistoricalDecisionApiResponse>
      cell={message}
      classNameOverrides="max-w-full w-full p-1.5"
    />
  );
};

const OutcomeColumnFilter: React.FC<{
  filters: DecisionsOutcomeFilter[] | undefined;
  propertyPath: string;
  type: SchemaTypes;
  enumValues: string[] | undefined;
  onReset: (outcomeTypeKey: string, fieldKey: string | null) => void;
  onSelect: (
    outcomeFilterKey: string,
    value: DecisionsOutcomeFilter["filter"],
  ) => void;
  onSelectDate: (
    outcomeTypeKey: string,
    value: SingleOutcomeFilter["filter"][],
  ) => void;
}> = ({
  filters,
  propertyPath,
  type,
  enumValues,
  onReset,
  onSelect,
  onSelectDate,
}) => {
  const [groupKey, property] = propertyPath.split(".");

  if (type === "boolean") {
    const filter = filters?.find(
      (filter) =>
        filter.outcome_type_key === groupKey &&
        isObject(filter.filter) &&
        filter.filter.property === property,
    ) as SingleOutcomeFilter | null;

    return (
      <span className="ml-1">
        <ColumnFilter
          dataLoc={`decision-history-outcome-filter-${groupKey}-${property}`}
          description="Filter by outcome"
          elements={BOOLEAN_FILTERS}
          isFiltering={Boolean(filter)}
          placement="bottom start"
          selected={filter?.filter.value.toString()}
          onResetRequest={() => onReset(groupKey, property)}
          onSelect={(value) =>
            onSelect(groupKey, {
              property,
              operator: "==",
              value: value === "true",
            })
          }
        />
      </span>
    );
  }

  if (type === "enum") {
    const filter = filters?.find(
      (filter) =>
        filter.outcome_type_key === groupKey &&
        isObject(filter.filter) &&
        filter.filter.property === property,
    ) as MultipleOutcomeFilter | null;

    return (
      <span className="ml-1">
        <EnumFilter
          dataLoc={`decision-history-outcome-filter-${groupKey}-${property}`}
          enumValues={enumValues ?? []}
          filter={filter}
          groupKey={groupKey}
          property={property}
          onApply={onSelect}
          onReset={onReset}
        />
      </span>
    );
  }

  if (type === "string" || type === "number" || type === "integer") {
    const filter = filters?.find(
      (filter) =>
        filter.outcome_type_key === groupKey &&
        isObject(filter.filter) &&
        filter.filter.property === property,
    ) as SingleOutcomeFilter | null;

    return (
      <OutcomeFieldFilter
        dataLoc={`decision-history-outcome-filter-${groupKey}-${property}`}
        filter={filter}
        operator={filter?.filter.operator}
        type={type}
        value={filter?.filter.value}
        onChange={(
          operator: EqualityOperator,
          value?: string | number | boolean,
        ) => {
          if (operator && !isNil(value)) {
            onSelect(groupKey, { property, operator, value });
            return;
          }

          if (filter) {
            onReset(groupKey, property);
          }
        }}
        onReset={() => onReset(groupKey, property)}
      />
    );
  }

  if (type === "date" || type === "datetime") {
    const dateFilters = (filters?.filter(
      (filter) =>
        filter.outcome_type_key === groupKey &&
        isObject(filter.filter) &&
        filter.filter.property === property,
    ) ?? []) as SingleOutcomeFilter[];

    return (
      <DateRangeFilter
        dataLoc={`decision-history-outcome-filter-${groupKey}-${property}`}
        filters={dateFilters}
        isFiltering={Boolean(dateFilters.length > 0)}
        property={property}
        onChange={(filters) => {
          if (filters.length > 0) {
            onSelectDate(groupKey, filters);
          }
        }}
        onReset={() => onReset(groupKey, property)}
      />
    );
  }

  return null;
};
